import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useGT } from "gt-react";
import {
  useQueriesStore,
  useServersStore,
  useAppStore,
  useSharedSyncStore,
  useExecutionTrackingStore,
  executeGraphQLRequest,
  executeRestRequest,
  collectRuntimeVaultValues,
  updateQueryInDb,
  saveQuerySnapshotAsSaved,
  upsertSharedPaneDraft,
  clearSharedPaneDraft,
  resolveSharedQueryConflict,
  dismissSharedQueryConflict,
  publishSharedServerIfConnected,
} from "../store";
import type { RestMethod, RestContentType, HttpDebugInfo } from "../store";
import { listHistoryForQuery, addHistoryEntry } from "../persistence";
import type { RequestType, RestParam, StoredHistoryEntry } from "../persistence";
import type { QueryRiskLevel } from "../persistence";
import { defaultQueryOperationConfig, defaultServerConnectionConfig } from "@spuerhund/shared/targets";
import { buildRestPath } from "@spuerhund/shared/queryMetadata";
import { executeRuntimeMongo, executeRuntimeSql } from "../runtimeClient";
import { createExecutionAbortController } from "../executionTimeout";
import { GraphQLEditor } from "./GraphQLEditor";
import { QueryParamsGrid } from "./QueryParamsGrid";
import { SplitPane } from "./SplitPane";
import { CodeMirrorEditor } from "./CodeMirrorEditor";
import { QueryRiskBar, QUERY_RISK_OPTIONS } from "./QueryRiskBar";
import { HeadersTable, Section, CopyButton, tryFormatJson, buildCurl } from "./ResultShared";
import { LazyRelationalResultGrid } from "./LazyRelationalResultGrid";
import { getRestBaseUrl } from "../serverUrls";
import { toast } from "./Toasts";
import { json } from "@codemirror/lang-json";
import {
  Play, Braces, X, ChevronDown, ChevronLeft, ChevronRight,
  AlertCircle, Terminal, AlertTriangle, Copy, Check,
  Save,
} from "lucide-react";
import { print, parse } from "graphql";
import {
  getQuerySyncAdoptionAction,
  queryDraftsEqual,
  toQueryDraftSnapshot,
  type QueryDraftSnapshot,
} from "../sharedSync";
import {
  restoreEditorStateFromHistory,
  restoreEditorStateFromQuery,
  restoreResultStateFromHistory,
} from "../historyReplay";
import type { QueryExecutionResult } from "../resultTypes";
import { extractSqlTabularRows } from "../resultTypes";

const NEW_CATEGORY_OPTION = "__new_category__";

const jsonExtensions = [json()];

const REST_METHODS: RestMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];
const METHODS_WITH_BODY = new Set(["POST", "PUT", "PATCH"]);
const CONTENT_TYPES: { value: RestContentType; label: string }[] = [
  { value: "json", label: "JSON" },
  { value: "xml", label: "XML" },
  { value: "text", label: "Text" },
  { value: "form-urlencoded", label: "Form" },
];
const METHOD_COLORS: Record<string, string> = {
  GET: "text-success",
  POST: "text-warning",
  PUT: "text-info",
  PATCH: "text-accent",
  DELETE: "text-danger",
  HEAD: "text-zinc-400",
  OPTIONS: "text-zinc-400",
};
function isCorsLikeError(error: string): boolean {
  return /CORS error:|Failed to fetch|NetworkError|Load failed/i.test(error);
}

function useElapsedTime(startedAt: number | null) {
  const [elapsed, setElapsed] = useState<number | null>(null);
  useEffect(() => {
    if (startedAt === null) { setElapsed(null); return; }
    const tick = () => setElapsed(Math.round((performance.now() - startedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return elapsed;
}

interface SecondaryQueryPaneProps {
  panelId: string;
  queryId: string;
  onClose: () => void;
}

export function SecondaryQueryPane({ panelId, queryId, onClose }: SecondaryQueryPaneProps) {
  const t = useGT();
  const queries = useQueriesStore((s) => s.queries);
  const query = queries.find((q) => q.id === queryId);
  const activePaneId = useAppStore((s) => s.activePaneId);
  const setShowRuntimeDialog = useAppStore((s) => s.setShowRuntimeDialog);

  // ── Editor state ──
  const [queryText, setQueryText] = useState("");
  const [variables, setVariables] = useState("{}");
  const [headers, setHeaders] = useState("{}");
  const [remarks, setRemarks] = useState("");
  const [riskLevel, setRiskLevel] = useState<QueryRiskLevel>("normal");
  const [activeTab, setActiveTab] = useState<"query" | "variables" | "headers" | "params">("query");
  const [requestType, setRequestType] = useState<RequestType>("graphql");
  const [restMethod, setRestMethod] = useState<RestMethod>("GET");
  const [restPath, setRestPath] = useState("/");
  const [restParams, setRestParams] = useState<RestParam[]>([]);
  const [restContentType, setRestContentType] = useState<RestContentType>("json");

  const isRest = requestType === "rest";
  const isMongo = requestType === "mongo";
  const isSql = requestType === "sql";
  const hasBody = METHODS_WITH_BODY.has(restMethod);

  // ── Result state ──
  const [result, setResult] = useState<QueryExecutionResult | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debug, setDebug] = useState<HttpDebugInfo | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [executionStartedAt, setExecutionStartedAt] = useState<number | null>(null);
  const elapsedSeconds = useElapsedTime(isExecuting ? executionStartedAt : null);
  const [copied, setCopied] = useState(false);
  const [displayValue, setDisplayValue] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveCategorySelection, setSaveCategorySelection] = useState<string>("Saved");
  const [saveCategory, setSaveCategory] = useState("Saved");

  // ── History state ──
  const [queryHistory, setQueryHistory] = useState<StoredHistoryEntry[]>([]);
  const [historyCursor, setHistoryCursor] = useState<number | null>(null);

  // ── Refs for auto-save ──
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryTextRef = useRef(queryText);
  const variablesRef = useRef(variables);
  const headersRef = useRef(headers);
  const remarksRef = useRef(remarks);
  const riskLevelRef = useRef(riskLevel);
  const restMethodRef = useRef(restMethod);
  const restPathRef = useRef(restPath);
  const restParamsRef = useRef(restParams);
  const restContentTypeRef = useRef(restContentType);
  const requestTypeRef = useRef(requestType);
  queryTextRef.current = queryText;
  variablesRef.current = variables;
  headersRef.current = headers;
  remarksRef.current = remarks;
  riskLevelRef.current = riskLevel;
  restMethodRef.current = restMethod;
  restPathRef.current = restPath;
  restParamsRef.current = restParams;
  restContentTypeRef.current = restContentType;
  requestTypeRef.current = requestType;

  // ── Server info ──
  const activeServerId = useServersStore((s) => s.activeServerId);
  const servers = useServersStore((s) => s.servers);
  const server = servers.find((s) => s.id === activeServerId);
  const sharedServerSync = useSharedSyncStore((s) => (activeServerId ? s.servers[activeServerId] : undefined));
  const syncedQuerySnapshot = useSharedSyncStore((s) => (queryId ? s.querySyncedSnapshots[queryId] ?? null : null));
  const queryConflict = useSharedSyncStore((s) => (queryId ? s.queryConflicts[queryId] ?? null : null));
  const recentRemoteUpdateAt = useSharedSyncStore((s) => (queryId ? s.recentRemoteUpdates[queryId] ?? null : null));
  const isProd = !!server?.isProduction;
  const baseUrl = server ? getRestBaseUrl(server) : "";
  const savedCategoryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          queries
            .filter((entry) => !entry.systemPreset && !entry.pinnedAt && entry.category?.trim())
            .map((entry) => entry.category!.trim()),
        ),
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    [queries],
  );
  const editorSnapshot = {
    queryText,
    variables,
    headers,
    remarks,
    riskLevel,
    requestType,
    ...(query?.operationConfig ? { operationConfig: query.operationConfig } : {}),
    restMethod,
    restPath,
    restContentType,
    restParams,
  } as const;
  const [baseSnapshot, setBaseSnapshot] = useState<QueryDraftSnapshot | null>(null);
  const [isApplyingRemoteSync, setIsApplyingRemoteSync] = useState(false);
  const [saveRemarks, setSaveRemarks] = useState("");
  const [saveRiskLevel, setSaveRiskLevel] = useState<QueryRiskLevel>("normal");
  const effectiveBaseSnapshot = baseSnapshot
    ?? (query && !query.systemPreset ? (syncedQuerySnapshot ?? toQueryDraftSnapshot(query)) : null);
  const isSharedSavedQuery = Boolean(query && !query.systemPreset && server?.sharedConfigId);
  const isLocalDirty = Boolean(
    !isApplyingRemoteSync
      && effectiveBaseSnapshot
      && query
      && !query.systemPreset
      && !queryDraftsEqual(effectiveBaseSnapshot, editorSnapshot),
  );
  const hasConflict = Boolean(queryConflict);
  const hasRecentRemoteUpdate = Boolean(recentRemoteUpdateAt && Date.now() - recentRemoteUpdateAt < 8_000);
  const isSavingCopy = Boolean(query && !query.systemPreset);
  const saveButtonLabel = isSavingCopy ? t("Kopie speichern") : t("Save");
  const saveButtonTitle = isSavingCopy ? t("Kopie speichern") : t("Save as saved query");
  const localSyncHint = isSharedSavedQuery && isLocalDirty ? t("Run to publish") : null;

  // ── Initialize from query ──
  const initializedQueryIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!query || initializedQueryIdRef.current === queryId) return;
    initializedQueryIdRef.current = queryId;
    setBaseSnapshot(syncedQuerySnapshot ?? toQueryDraftSnapshot(query));
    setQueryText(query.queryText);
    setVariables(query.variables);
    setHeaders(query.headers);
    setRemarks(query.remarks ?? "");
    setRiskLevel(query.riskLevel ?? "normal");
    setRequestType(query.requestType ?? "graphql");
    setRestMethod(query.restMethod ?? "GET");
    setRestPath(query.restPath ?? "/");
    setRestParams(query.restParams ?? []);
    setRestContentType(query.restContentType ?? "json");
    setResult(null);
    setError(null);
    setDebug(null);
    setDisplayValue(null);
    setHistoryCursor(null);
  }, [query, queryId, syncedQuerySnapshot]);

  useEffect(() => {
    if (!query || !isSharedSavedQuery) {
      clearSharedPaneDraft(panelId);
      return;
    }

    upsertSharedPaneDraft(panelId, {
      serverId: activeServerId,
      queryId,
      dirty: isLocalDirty,
      snapshot: editorSnapshot,
    });
  }, [
    activeServerId,
    editorSnapshot.headers,
    editorSnapshot.queryText,
    editorSnapshot.remarks,
    editorSnapshot.riskLevel,
    editorSnapshot.requestType,
    editorSnapshot.restContentType,
    editorSnapshot.restMethod,
    editorSnapshot.restParams,
    editorSnapshot.restPath,
    editorSnapshot.variables,
    isLocalDirty,
    isSharedSavedQuery,
    panelId,
    query?.id,
    queryId,
  ]);

  useEffect(() => () => clearSharedPaneDraft(panelId), [panelId]);

  useEffect(() => {
    if (!query || query.systemPreset || !effectiveBaseSnapshot) return;
    const nextSyncedSnapshot = syncedQuerySnapshot ?? toQueryDraftSnapshot(query);
    const action = getQuerySyncAdoptionAction({
      baseSnapshot: effectiveBaseSnapshot,
      editorSnapshot,
      syncedSnapshot: nextSyncedSnapshot,
    });
    if (action === "none") return;
    if (action === "applySynced") {
      setIsApplyingRemoteSync(true);
      setQueryText(query.queryText);
      setVariables(query.variables);
      setHeaders(query.headers);
      setRemarks(query.remarks ?? "");
      setRiskLevel(query.riskLevel ?? "normal");
      setRequestType(query.requestType ?? "graphql");
      setRestMethod(query.restMethod ?? "GET");
      setRestPath(query.restPath ?? "/");
      setRestParams(query.restParams ?? []);
      setRestContentType(query.restContentType ?? "json");
      upsertSharedPaneDraft(panelId, {
        serverId: activeServerId,
        queryId,
        dirty: false,
        snapshot: nextSyncedSnapshot,
      });
    }
    setBaseSnapshot(nextSyncedSnapshot);
  }, [activeServerId, effectiveBaseSnapshot, editorSnapshot, panelId, query, queryId, syncedQuerySnapshot]);

  useEffect(() => {
    if (!isApplyingRemoteSync || !effectiveBaseSnapshot) return;
    if (!queryDraftsEqual(effectiveBaseSnapshot, editorSnapshot)) return;
    setIsApplyingRemoteSync(false);
  }, [effectiveBaseSnapshot, editorSnapshot, isApplyingRemoteSync]);

  useEffect(() => {
    if (!query || query.systemPreset || !isSharedSavedQuery) return;
    if (hasConflict || isLocalDirty) return;
    const querySnapshot = toQueryDraftSnapshot(query);
    if (queryDraftsEqual(editorSnapshot, querySnapshot)) return;
    setIsApplyingRemoteSync(true);
    setQueryText(query.queryText);
    setVariables(query.variables);
    setHeaders(query.headers);
    setRemarks(query.remarks ?? "");
    setRiskLevel(query.riskLevel ?? "normal");
    setRequestType(query.requestType ?? "graphql");
    setRestMethod(query.restMethod ?? "GET");
    setRestPath(query.restPath ?? "/");
    setRestParams(query.restParams ?? []);
    setRestContentType(query.restContentType ?? "json");
    setBaseSnapshot(syncedQuerySnapshot ?? querySnapshot);
    upsertSharedPaneDraft(panelId, {
      serverId: activeServerId,
      queryId,
      dirty: false,
      snapshot: syncedQuerySnapshot ?? querySnapshot,
    });
  }, [
    activeServerId,
    editorSnapshot,
    hasConflict,
    isLocalDirty,
    isSharedSavedQuery,
    panelId,
    query,
    queryId,
    syncedQuerySnapshot,
  ]);

  // ── Load history ──
  useEffect(() => {
    if (activeServerId && queryId) {
      listHistoryForQuery(activeServerId, queryId, 100).then(setQueryHistory);
    }
  }, [activeServerId, queryId]);

  const refreshLocalHistory = useCallback(async () => {
    if (!activeServerId) return;
    const updated = await listHistoryForQuery(activeServerId, queryId, 100);
    setQueryHistory(updated);
    setHistoryCursor(null);
  }, [activeServerId, queryId]);

  useEffect(() => {
    if (!showSaveDialog) return;
    const defaultExistingCategory = savedCategoryOptions[0] ?? "Saved";
    if (query && !query.systemPreset) {
      setSaveName(query.name);
      setSaveRemarks(query.remarks ?? "");
      setSaveRiskLevel(query.riskLevel ?? "normal");
      const initialCategory = query.category?.trim();
      if (initialCategory) {
        setSaveCategorySelection(initialCategory);
        setSaveCategory(initialCategory);
      } else {
        setSaveCategorySelection(NEW_CATEGORY_OPTION);
        setSaveCategory(defaultExistingCategory);
      }
    } else {
      setSaveName("");
      setSaveRemarks("");
      setSaveRiskLevel("normal");
      setSaveCategorySelection(savedCategoryOptions.includes("Saved") ? "Saved" : NEW_CATEGORY_OPTION);
      setSaveCategory(defaultExistingCategory);
    }
  }, [query, savedCategoryOptions, showSaveDialog]);

  // ── Auto-save ──
  const localScheduleAutoSave = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      updateQueryInDb(queryId, {
        queryText: queryTextRef.current,
        variables: variablesRef.current,
        headers: headersRef.current,
        remarks: remarksRef.current,
        riskLevel: riskLevelRef.current,
        requestType: requestTypeRef.current,
        restMethod: restMethodRef.current,
        restPath: restPathRef.current,
        restParams: restParamsRef.current,
        restContentType: restContentTypeRef.current,
      }, { syncShared: false });
      saveTimeoutRef.current = null;
    }, 1000);
  }, [queryId]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        updateQueryInDb(queryId, {
          queryText: queryTextRef.current,
          variables: variablesRef.current,
          headers: headersRef.current,
          remarks: remarksRef.current,
          riskLevel: riskLevelRef.current,
          requestType: requestTypeRef.current,
          restMethod: restMethodRef.current,
          restPath: restPathRef.current,
          restParams: restParamsRef.current,
          restContentType: restContentTypeRef.current,
        }, { syncShared: false });
      }
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [queryId]);

  // ── History navigation ──
  const canGoBack = queryHistory.length > 0 && (historyCursor === null || historyCursor < queryHistory.length - 1);
  const canGoForward = historyCursor !== null;
  const isViewingHistory = historyCursor !== null;
  const historyPositionLabel = historyCursor === null
    ? "live"
    : `${queryHistory.length - historyCursor} / ${queryHistory.length}`;

  const navigateBack = useCallback(() => {
    if (!canGoBack) return;
    const newCursor = historyCursor === null ? 0 : historyCursor + 1;
    setHistoryCursor(newCursor);
    const entry = queryHistory[newCursor]!;
    const restoredEditor = restoreEditorStateFromHistory(entry);
    setQueryText(restoredEditor.queryText);
    setVariables(restoredEditor.variables);
    setHeaders(restoredEditor.headers);
    setRequestType(restoredEditor.requestType);
    setRestMethod(restoredEditor.restMethod);
    setRestPath(restoredEditor.restPath);
    setRestParams(restoredEditor.restParams);
    setRestContentType(restoredEditor.restContentType);
    const restoredResult = restoreResultStateFromHistory(entry);
    setResult(restoredResult.result);
    setError(restoredResult.error);
  }, [canGoBack, historyCursor, queryHistory]);

  const navigateForward = useCallback(() => {
    if (historyCursor === null) return;
    if (historyCursor <= 0) {
      setHistoryCursor(null);
      // Restore live state from query
      if (query) {
        const restoredEditor = restoreEditorStateFromQuery(query);
        setQueryText(restoredEditor.queryText);
        setVariables(restoredEditor.variables);
        setHeaders(restoredEditor.headers);
        setRequestType(restoredEditor.requestType);
        setRestMethod(restoredEditor.restMethod);
        setRestPath(restoredEditor.restPath);
        setRestParams(restoredEditor.restParams);
        setRestContentType(restoredEditor.restContentType);
      }
      setResult(null);
      setError(null);
      return;
    }
    const newCursor = historyCursor - 1;
    setHistoryCursor(newCursor);
    const entry = queryHistory[newCursor]!;
    const restoredEditor = restoreEditorStateFromHistory(entry);
    setQueryText(restoredEditor.queryText);
    setVariables(restoredEditor.variables);
    setHeaders(restoredEditor.headers);
    setRequestType(restoredEditor.requestType);
    setRestMethod(restoredEditor.restMethod);
    setRestPath(restoredEditor.restPath);
    setRestParams(restoredEditor.restParams);
    setRestContentType(restoredEditor.restContentType);
    const restoredResult = restoreResultStateFromHistory(entry);
    setResult(restoredResult.result);
    setError(restoredResult.error);
  }, [historyCursor, queryHistory, query]);

  // ── Execute ──
  const handleExecute = useCallback(async () => {
    if (!activeServerId || !server) return;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (query && !query.systemPreset) {
      await updateQueryInDb(queryId, {
        queryText: queryTextRef.current,
        variables: variablesRef.current,
        headers: headersRef.current,
        remarks: remarksRef.current,
        riskLevel: riskLevelRef.current,
        requestType: requestTypeRef.current,
        restMethod: restMethodRef.current,
        restPath: restPathRef.current,
        restParams: restParamsRef.current,
        restContentType: restContentTypeRef.current,
      }, { syncShared: false });
      if (server.sharedConfigId) {
        const publishResult = await publishSharedServerIfConnected(activeServerId);
        if (publishResult === "conflict") {
          toast("Remote changes were detected. Resolve the conflict before executing.", "error");
          return;
        }
        if (publishResult === "failed") {
          return;
        }
      }
    }

    const { controller, cleanup } = createExecutionAbortController(server?.timeoutMs);
    abortControllerRef.current = controller;
    setExecutionStartedAt(performance.now());
    setIsExecuting(true);
    setError(null);
    setDisplayValue(null);
    setDebug(null);
    useExecutionTrackingStore.getState().markExecuting(queryId);

    // REST execution URL combines the clean path with the separate params field.
    const restExecutionPath = buildRestPath(restPathRef.current, restParamsRef.current);

    try {
      if (requestTypeRef.current === "rest") {
        const res = await executeRestRequest({
          server,
          method: restMethodRef.current,
          path: restExecutionPath,
          body: queryTextRef.current,
          contentType: restContentTypeRef.current,
          customHeaders: headersRef.current,
          signal: controller.signal,
        });

        setDebug(res.debug);

        if (res.errorMessage && res.statusCode === 0) {
          setError(res.errorMessage);
          // Save error to history
          await addHistoryEntry({
            serverId: activeServerId,
            queryId,
            queryText: queryTextRef.current,
            variables: variablesRef.current,
            headers: headersRef.current,
            durationMs: res.durationMs,
            errorMessage: res.errorMessage,
            executedAt: new Date().toISOString(),
            requestType: "rest",
            restMethod: restMethodRef.current,
            restPath: restExecutionPath,
            restContentType: restContentTypeRef.current,
          });
          await refreshLocalHistory();
          return;
        }

        let parsedData: unknown = undefined;
        const isJson = res.responseContentType.includes("json");
        if (isJson) {
          try { parsedData = JSON.parse(res.responseText); } catch { /* raw */ }
        }

        setResult({
          requestType: "rest",
          ...(parsedData !== undefined ? { data: parsedData } : {}),
          statusCode: res.statusCode,
          durationMs: res.durationMs,
          responseHeaders: res.responseHeaders,
          responseBody: res.responseText,
          responseContentType: res.responseContentType,
        });

        // Save to history
        await addHistoryEntry({
          serverId: activeServerId,
          queryId,
          queryText: queryTextRef.current,
          variables: variablesRef.current,
          headers: headersRef.current,
          responseBody: res.responseText,
          responseContentType: res.responseContentType,
          statusCode: res.statusCode,
          durationMs: res.durationMs,
          executedAt: new Date().toISOString(),
          requestType: "rest",
          restMethod: restMethodRef.current,
          restPath: restExecutionPath,
          restContentType: restContentTypeRef.current,
          ...(isJson ? { responseJson: res.responseText } : {}),
          ...(res.errorMessage ? { errorMessage: res.errorMessage } : {}),
        });
      } else if (requestTypeRef.current === "mongo") {
        const connection = server ? defaultServerConnectionConfig(server) : null;
        const operationConfig = query ? defaultQueryOperationConfig(query) : undefined;
        if (!server || connection?.kind !== "mongo" || operationConfig?.kind !== "mongo") {
          throw new Error("Mongo execution requires a Mongo server and saved Mongo query metadata.");
        }

        const res = await executeRuntimeMongo({
          serverId: server.id,
          connectionUri: connection.connectionUri,
          database: connection.database,
          collection: operationConfig.collection,
          operation: operationConfig.operation,
          queryText: queryTextRef.current,
          variables: variablesRef.current,
          ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
          vaultValues: await collectRuntimeVaultValues(server, {
            rawHeaders: headersRef.current,
            rawVariables: variablesRef.current,
            rawQueryText: queryTextRef.current,
            operationConfig,
          }),
        }, controller.signal);

        setResult({
          requestType: "mongo",
          ...(res.data !== undefined ? { data: res.data } : {}),
          statusCode: res.statusCode,
          durationMs: res.durationMs,
          responseBody: res.responseText,
          responseContentType: "application/json",
        });

        if (res.errorMessage) {
          setError(res.errorMessage);
        }

        await addHistoryEntry({
          serverId: activeServerId,
          queryId,
          queryText: queryTextRef.current,
          variables: variablesRef.current,
          headers: headersRef.current,
          responseBody: res.responseText,
          responseContentType: "application/json",
          statusCode: res.statusCode,
          durationMs: res.durationMs,
          ...(res.errorMessage ? { errorMessage: res.errorMessage } : {}),
          executedAt: new Date().toISOString(),
          requestType: "mongo",
          operationConfig,
        });
      } else if (requestTypeRef.current === "sql") {
        const connection = server ? defaultServerConnectionConfig(server) : null;
        const operationConfig = query ? defaultQueryOperationConfig(query) : undefined;
        if (!server || connection?.kind !== "postgres") {
          throw new Error("SQL execution requires a PostgreSQL server.");
        }

        const res = await executeRuntimeSql({
          serverId: server.id,
          connectionUri: connection.connectionUri,
          statementText: queryTextRef.current,
          paramsJson: variablesRef.current,
          ...((operationConfig?.kind === "sql" ? operationConfig.defaultSchema : connection.defaultSchema)
            ? { defaultSchema: (operationConfig?.kind === "sql" ? operationConfig.defaultSchema : connection.defaultSchema)! }
            : {}),
          ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
          vaultValues: await collectRuntimeVaultValues(server, {
            rawHeaders: headersRef.current,
            rawVariables: variablesRef.current,
            rawQueryText: queryTextRef.current,
            ...(operationConfig?.kind === "sql" ? { operationConfig } : {}),
          }),
        }, controller.signal);

        setResult({
          requestType: "sql",
          ...(res.rows ? { data: res.rows } : {}),
          statusCode: res.statusCode,
          durationMs: res.durationMs,
          responseBody: res.responseText,
          responseContentType: "application/json",
          ...(res.command ? { command: res.command } : {}),
          ...(typeof res.rowCount === "number" ? { rowCount: res.rowCount } : {}),
        });

        if (res.errorMessage) {
          setError(res.errorMessage);
        }

        await addHistoryEntry({
          serverId: activeServerId,
          queryId,
          queryText: queryTextRef.current,
          variables: variablesRef.current,
          headers: headersRef.current,
          responseBody: res.responseText,
          responseContentType: "application/json",
          statusCode: res.statusCode,
          durationMs: res.durationMs,
          ...(res.errorMessage ? { errorMessage: res.errorMessage } : {}),
          executedAt: new Date().toISOString(),
          requestType: "sql",
          ...(operationConfig?.kind === "sql" ? { operationConfig } : {}),
        });
      } else {
        if (!queryTextRef.current.trim()) return;
        const res = await executeGraphQLRequest({
          server,
          queryText: queryTextRef.current,
          variables: variablesRef.current,
          customHeaders: headersRef.current,
          signal: controller.signal,
        });

        setDebug(res.debug);

        if (res.errorMessage) {
          setError(res.errorMessage);
          await addHistoryEntry({
            serverId: activeServerId,
            queryId,
            queryText: queryTextRef.current,
            variables: variablesRef.current,
            headers: headersRef.current,
            durationMs: res.durationMs,
            errorMessage: res.errorMessage,
            executedAt: new Date().toISOString(),
            requestType: "graphql",
          });
          await refreshLocalHistory();
          return;
        }

        setResult({
          requestType: "graphql",
          data: res.data,
          ...(res.errors ? { errors: res.errors } : {}),
          statusCode: res.statusCode,
          durationMs: res.durationMs,
        });

        await addHistoryEntry({
          serverId: activeServerId,
          queryId,
          queryText: queryTextRef.current,
          variables: variablesRef.current,
          headers: headersRef.current,
          responseJson: res.responseText,
          statusCode: res.statusCode,
          durationMs: res.durationMs,
          executedAt: new Date().toISOString(),
          requestType: "graphql",
          ...(res.errors ? { errorMessage: JSON.stringify(res.errors) } : {}),
        });
      }

      // Reload history after execution
      await refreshLocalHistory();
    } finally {
      cleanup();
      abortControllerRef.current = null;
      setExecutionStartedAt(null);
      setIsExecuting(false);
      useExecutionTrackingStore.getState().markDone(queryId);
    }
  }, [activeServerId, queryId, refreshLocalHistory, server]);

  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
  }, []);

  const handleSaveQuery = useCallback(async () => {
    if (!activeServerId) return;
    try {
      const resolvedCategory = saveCategorySelection === NEW_CATEGORY_OPTION
        ? saveCategory.trim()
        : saveCategorySelection;
      await saveQuerySnapshotAsSaved({
        serverId: activeServerId,
        name: saveName,
        saveTo: "category",
        category: resolvedCategory,
        snapshot: {
          queryText,
          variables,
          headers,
          remarks: saveRemarks,
          riskLevel: saveRiskLevel,
          requestType,
          ...(query?.operationConfig ? { operationConfig: query.operationConfig } : {}),
          restMethod,
          restPath,
          restParams,
          restContentType,
        },
      });
      setShowSaveDialog(false);
      toast("Query saved", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), "error");
    }
  }, [
    activeServerId,
    headers,
    queryText,
    requestType,
    restContentType,
    restMethod,
    restPath,
    restParams,
    saveCategory,
    saveCategorySelection,
    saveName,
    saveRemarks,
    saveRiskLevel,
    variables,
  ]);

  const handleKeepLocalVersion = useCallback(async () => {
    if (!query) return;
    try {
      const resolved = await resolveSharedQueryConflict(query.id, "pushLocal", editorSnapshot);
      if (resolved.appliedSnapshot) {
        setBaseSnapshot(resolved.appliedSnapshot);
        upsertSharedPaneDraft(panelId, {
          serverId: activeServerId,
          queryId: query.id,
          dirty: false,
          snapshot: resolved.appliedSnapshot,
        });
      }
      toast("Local version pushed", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), "error");
    }
  }, [activeServerId, editorSnapshot, panelId, query]);

  const handleUseServerVersion = useCallback(async () => {
    if (!query) return;
    try {
      const resolved = await resolveSharedQueryConflict(query.id, "useRemote");
      if (resolved.appliedSnapshot) {
        setQueryText(resolved.appliedSnapshot.queryText);
        setVariables(resolved.appliedSnapshot.variables);
        setHeaders(resolved.appliedSnapshot.headers);
        setRequestType(resolved.appliedSnapshot.requestType ?? "graphql");
        setRestMethod(resolved.appliedSnapshot.restMethod ?? "GET");
        setRestPath(resolved.appliedSnapshot.restPath ?? "/");
        setRestParams(resolved.appliedSnapshot.restParams ?? []);
        setRestContentType(resolved.appliedSnapshot.restContentType ?? "json");
        setBaseSnapshot(resolved.appliedSnapshot);
        upsertSharedPaneDraft(panelId, {
          serverId: activeServerId,
          queryId: query.id,
          dirty: false,
          snapshot: resolved.appliedSnapshot,
        });
      }
      toast("Server version applied", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), "error");
    }
  }, [activeServerId, panelId, query]);

  const handleDismissConflict = useCallback(() => {
    dismissSharedQueryConflict(queryId);
  }, [queryId]);

  // ── Prettify ──
  const handlePrettify = useCallback(() => {
    if (isRest) {
      if (activeTab === "query" && restContentType === "json" && queryText.trim()) {
        try {
          const formatted = JSON.stringify(JSON.parse(queryText), null, 2);
          setQueryText(formatted);
          localScheduleAutoSave();
        } catch { /* invalid JSON */ }
      } else if (activeTab === "headers") {
        try {
          const formatted = JSON.stringify(JSON.parse(headers), null, 2);
          setHeaders(formatted);
          localScheduleAutoSave();
        } catch { /* invalid JSON */ }
      }
    } else if (isMongo) {
      if (activeTab === "query") {
        try {
          const formatted = JSON.stringify(JSON.parse(queryText), null, 2);
          setQueryText(formatted);
          localScheduleAutoSave();
        } catch { /* invalid JSON */ }
      } else {
        const raw = activeTab === "variables" ? variables : headers;
        try {
          const formatted = JSON.stringify(JSON.parse(raw), null, 2);
          if (activeTab === "variables") setVariables(formatted);
          else setHeaders(formatted);
          localScheduleAutoSave();
        } catch { /* invalid JSON */ }
      }
    } else {
      if (activeTab === "query") {
        try {
          const formatted = print(parse(queryText));
          setQueryText(formatted);
          localScheduleAutoSave();
        } catch { /* invalid query */ }
      } else {
        const raw = activeTab === "variables" ? variables : headers;
        try {
          const formatted = JSON.stringify(JSON.parse(raw), null, 2);
          if (activeTab === "variables") setVariables(formatted);
          else setHeaders(formatted);
          localScheduleAutoSave();
        } catch { /* invalid JSON */ }
      }
    }
  }, [activeTab, queryText, variables, headers, localScheduleAutoSave, isMongo, isRest, restContentType]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    if (activePaneId !== panelId) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        if (e.defaultPrevented) return;
        e.preventDefault();
        handleExecute();
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "f" || e.key === "F")) {
        if (e.defaultPrevented) return;
        e.preventDefault();
        handlePrettify();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activePaneId, handleExecute, handlePrettify, panelId]);

  // ── Tab config ──
  const paramCount = restParams.filter((p) => p.key.trim()).length;

  const tabs = isRest
    ? [
        { id: "query" as const, label: "Body" },
        { id: "params" as const, label: paramCount > 0 ? `Params (${paramCount})` : "Params" },
        { id: "headers" as const, label: "Headers" },
      ]
    : [
        { id: "query" as const, label: "Query" },
        { id: "variables" as const, label: "Variables" },
        { id: "headers" as const, label: "Headers" },
      ];

  useEffect(() => {
    if (isRest && activeTab === "variables") setActiveTab("query");
    if (!isRest && activeTab === "params") setActiveTab("query");
  }, [isRest, activeTab]);

  const currentValue = activeTab === "query" ? queryText : activeTab === "variables" ? variables : headers;

  const handleChange = useCallback((value: string) => {
    if (activeTab === "query") setQueryText(value);
    else if (activeTab === "variables") setVariables(value);
    else setHeaders(value);
    localScheduleAutoSave();
  }, [activeTab, localScheduleAutoSave]);

  const editorMode: "graphql" | "json" | "text" = isRest
    ? (activeTab === "query" ? (restContentType === "json" ? "json" : "text") : "json")
    : activeTab === "query"
      ? isMongo
        ? "json"
        : isSql
          ? "text"
          : "graphql"
      : "json";

  const canPrettify = isRest
    ? (activeTab === "query" && restContentType === "json") || activeTab === "headers"
    : !isSql;

  const placeholder =
    isRest
      ? activeTab === "query"
        ? hasBody ? '{"key": "value"}' : ""
        : '{"Authorization": "Bearer ..."}'
      : activeTab === "query"
        ? isMongo
          ? '{"field": "value"}'
          : isSql
            ? "select * from table where id = $1"
            : t("Write your GraphQL query here")
        : activeTab === "variables"
          ? '{"key": "value"}'
          : '{"Authorization": "Bearer ..."}';

  // ── Result formatting ──
  const isJsonResponse = useMemo(() => {
    if (!result) return true;
    if (result.responseContentType) return result.responseContentType.includes("json");
    return result.data !== undefined;
  }, [result]);

  const rawResult = useMemo(() => {
    if (!result) return null;
    if (result.requestType === "sql" && result.responseBody !== undefined) return result.responseBody;
    if (result.responseBody !== undefined && !isJsonResponse) return result.responseBody;
    try {
      const output: Record<string, unknown> = {};
      if (result.data !== undefined) output.data = result.data;
      if (result.errors?.length) output.errors = result.errors;
      return JSON.stringify(Object.keys(output).length ? output : result, null, 2);
    } catch { return result.responseBody ?? String(result); }
  }, [result, isJsonResponse]);

  const gqlErrors = useMemo(() => {
    if (!result?.errors?.length) return null;
    return result.errors as Array<{
      message?: string;
      locations?: Array<{ line: number; column: number }>;
      path?: Array<string | number>;
      extensions?: Record<string, unknown>;
    }>;
  }, [result]);

  const sqlRows = useMemo(() => extractSqlTabularRows(result), [result]);
  const sqlRowCount = result?.requestType === "sql"
    ? result.rowCount ?? sqlRows?.length ?? 0
    : null;

  const shownValue = displayValue ?? rawResult;

  const responseSize = useMemo(() => {
    if (!shownValue) return null;
    const bytes = new Blob([shownValue]).size;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, [shownValue]);

  useEffect(() => { setDisplayValue(null); }, [result]);

  const handleCopy = useCallback(async () => {
    if (!shownValue) return;
    await navigator.clipboard.writeText(shownValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [shownValue]);

  const handlePrettifyResult = useCallback(() => {
    if (!shownValue) return;
    try {
      const prettified = JSON.stringify(JSON.parse(shownValue), null, 2);
      setDisplayValue(prettified);
    } catch { /* ignore */ }
  }, [shownValue]);

  const statusColor =
    result?.statusCode != null
        ? result.statusCode >= 200 && result.statusCode < 300
        ? "text-success"
        : result.statusCode >= 400
          ? "text-danger"
          : "text-warning"
      : "text-zinc-500";

  const hasResponseHeaders = debug?.response?.headers && Object.keys(debug.response.headers).length > 0;
  const syncDot = !isSharedSavedQuery
    ? null
    : hasConflict
      ? { label: t("Conflict"), className: "bg-danger" }
      : isLocalDirty
        ? { label: t("Local only"), className: "bg-warning" }
        : sharedServerSync?.pushing
          ? { label: t("Publishing..."), className: "bg-warning" }
          : sharedServerSync?.connection === "offline"
            ? { label: t("Offline"), className: "bg-danger" }
            : hasRecentRemoteUpdate
              ? { label: t("Remote update"), className: "bg-success" }
              : { label: t("Synced"), className: "bg-success" };
  const remoteActorHint = isSharedSavedQuery
    && hasRecentRemoteUpdate
    && sharedServerSync?.recentRemoteQueryIds.includes(queryId)
    && sharedServerSync.lastRemoteActor
    ? t("{actor} is also here", { actor: sharedServerSync.lastRemoteActor })
    : null;

  if (!query) return null;

  return (
    <div className="flex flex-1 flex-col overflow-hidden border-b border-border last:border-b-0">
      {/* Name header with actions */}
      <div className="border-b border-border bg-surface-raised">
        <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-1">
          <div className="flex min-w-0 items-center gap-2">
            {syncDot && (
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${syncDot.className}`}
                title={syncDot.label}
                aria-label={syncDot.label}
              />
            )}
            <span className="truncate text-xs font-medium text-zinc-300" title={query.name}>
              {query.name}
            </span>
            {localSyncHint && (
              <span className="truncate text-[11px] text-warning" title={localSyncHint}>
                ({localSyncHint})
              </span>
            )}
            {remoteActorHint && (
              <span className="truncate text-[11px] text-zinc-500" title={remoteActorHint}>
                ({remoteActorHint})
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {activeServerId && (
              <button
                onClick={() => setShowSaveDialog(true)}
                className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 transition-colors"
                title={saveButtonTitle}
              >
                <Save size={12} />
                <span>{saveButtonLabel}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 transition-colors"
              title={t("Close panel")}
            >
              <span>{t("Close")}</span>
            </button>
          </div>
        </div>
        {hasConflict && queryConflict && (
          <div className="flex flex-wrap items-center gap-2 border-t border-danger/20 bg-danger-bg px-3 py-2">
            <div className="text-[11px] text-danger">
              Remote update by {queryConflict.updatedByDisplayName || queryConflict.updatedByClientId || "someone"} is waiting.
              {queryConflict.remoteDeleted ? " The server deleted this query." : " Keep your local work or use the server version."}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => void handleKeepLocalVersion()}
                className="rounded bg-brand px-2.5 py-1 text-[11px] font-semibold text-zinc-950 hover:bg-brand-light"
              >
                Meine pushen
              </button>
              <button
                onClick={() => void handleUseServerVersion()}
                className="rounded border border-danger/30 px-2.5 py-1 text-[11px] font-medium text-danger hover:bg-danger-bg/80"
              >
                Server-Version verwenden
              </button>
              <button
                onClick={handleDismissConflict}
                className="text-[11px] text-zinc-500 hover:text-zinc-300"
              >
                später
              </button>
            </div>
          </div>
        )}
      </div>

      <SplitPane direction="vertical">
        {/* ── Editor section (mirrors QueryEditor) ── */}
        <div className="flex flex-1 flex-col border-b border-border overflow-hidden">
          {/* REST URL bar */}
          {isRest && (
            <div className="flex shrink-0 items-center gap-1.5 border-b border-border bg-surface-raised px-2 py-1.5">
              <div className="relative">
                <select
                  value={restMethod}
                  onChange={(e) => { setRestMethod(e.target.value as RestMethod); localScheduleAutoSave(); }}
                  className={`appearance-none rounded border border-zinc-700 bg-surface py-0.5 pl-2 pr-6 text-xs font-bold ${METHOD_COLORS[restMethod] ?? "text-zinc-400"} cursor-pointer hover:border-zinc-500 focus:border-accent focus:outline-none`}
                >
                  {REST_METHODS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <ChevronDown size={10} className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              </div>
              <span className="shrink-0 text-[11px] text-zinc-600 font-mono">{baseUrl}</span>
              <input
                type="text"
                value={restPath}
                onChange={(e) => { setRestPath(e.target.value); localScheduleAutoSave(); }}
                placeholder={t("/path")}
                className="flex-1 rounded border border-zinc-700 bg-surface px-2 py-0.5 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
              />
            </div>
          )}

          {/* Tab bar */}
          <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface-raised px-2">
            <div className="flex items-center">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    activeTab === tab.id
                      ? "border-b-2 border-brand text-zinc-100"
                      : "text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}

              {/* Content-Type selector for REST body tab */}
              {isRest && activeTab === "query" && hasBody && (
                <div className="relative ml-2">
                  <select
                    value={restContentType}
                    onChange={(e) => { setRestContentType(e.target.value as RestContentType); localScheduleAutoSave(); }}
                    className="appearance-none rounded border border-zinc-700 bg-surface py-0.5 pl-2 pr-5 text-[10px] text-zinc-400 cursor-pointer hover:border-zinc-500 focus:border-accent focus:outline-none"
                  >
                    {CONTENT_TYPES.map((ct) => (
                      <option key={ct.value} value={ct.value}>{ct.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={8} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-zinc-600" />
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              {/* History navigation */}
              <div className="flex items-center rounded border border-zinc-700 transition-colors">
                <button
                  onClick={navigateBack}
                  disabled={!canGoBack}
                  className="rounded-l p-1 text-zinc-400 hover:text-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t("Step back through execution history")}
                >
                  <ChevronLeft size={14} />
                </button>
                <span
                  className={`min-w-[3rem] text-center text-[10px] font-medium ${
                    isViewingHistory
                      ? "bg-brand-dim px-1.5 py-0.5 text-brand-light"
                      : "text-zinc-600"
                  }`}
                >
                  {historyPositionLabel}
                </span>
                <button
                  onClick={navigateForward}
                  disabled={!canGoForward}
                  className="rounded-r p-1 text-zinc-400 hover:text-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t("Step forward through execution history")}
                >
                  <ChevronRight size={14} />
                </button>
              </div>

              {/* Prettify */}
              {canPrettify && (
                <button
                  onClick={handlePrettify}
                  title={!isRest && activeTab === "query" ? "Format query — ⌘⇧F" : "Format JSON — ⌘⇧F"}
                  className="rounded border border-zinc-700 p-1 text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-200"
                >
                  <Braces size={14} />
                </button>
              )}

              {/* Execute / Cancel */}
              {isExecuting ? (
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-1.5 rounded bg-danger-strong px-3 py-1 text-xs font-semibold text-on-danger hover:bg-danger-strong-hover"
                >
                  <X size={12} />
                  Cancel
                  {elapsedSeconds !== null && (
                    <span className="ml-0.5 font-mono text-[10px] font-normal opacity-80">
                      {elapsedSeconds}s
                    </span>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleExecute}
                  disabled={!activeServerId || (!isRest && !queryText.trim())}
                  className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${
                    isProd
                      ? "border border-danger/40 bg-warning text-zinc-950 hover:border-danger hover:bg-warning/90"
                      : "bg-brand text-zinc-900 hover:bg-brand-light"
                  }`}
                >
                  <Play size={12} />
                  {isRest ? "Send" : "Execute"}
                  <kbd className={`ml-1 rounded px-1 py-0.5 text-[9px] font-normal ${
                    isProd ? "bg-danger-bg text-danger" : "bg-brand-dim text-brand-light"
                  }`}>
                    ⌘↩
                  </kbd>
                </button>
              )}
            </div>
          </div>

          {/* Editor area */}
          {activeTab === "params" ? (
            <QueryParamsGrid
              params={restParams}
              onChange={(params) => { setRestParams(params); localScheduleAutoSave(); }}
            />
          ) : isRest && activeTab === "query" && !hasBody ? (
            <div className="flex flex-1 items-center justify-center text-zinc-600 text-sm">
              No body for {restMethod} requests
            </div>
          ) : (
            <GraphQLEditor
              value={currentValue}
              onChange={handleChange}
              mode={editorMode}
              placeholder={placeholder}
              onExecute={handleExecute}
            />
          )}
          {query && !query.systemPreset ? (
            <QueryRiskBar
              remarks={remarks}
              riskLevel={riskLevel}
              isProduction={isProd}
              onRemarksChange={(value) => {
                setRemarks(value);
                localScheduleAutoSave();
              }}
              onRiskLevelChange={(value) => {
                setRiskLevel(value);
                localScheduleAutoSave();
              }}
            />
          ) : isProd ? <div className="hazard-stripe-h" /> : null}
        </div>

        {/* ── Result section (mirrors ResultPane) ── */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Status bar */}
          <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface-raised px-4 py-1.5">
            <span className="text-xs font-medium text-zinc-500">{t("Result")}</span>
            <div className="flex items-center gap-3">
              {result && (
                <>
                  {result.statusCode != null && (
                    <span className={`text-xs font-mono font-medium ${statusColor}`}>
                      {result.statusCode}
                    </span>
                  )}
                  {result.durationMs != null && (
                    <span className="text-xs text-zinc-500">{result.durationMs}ms</span>
                  )}
                  {result.requestType === "sql" && result.command && (
                    <span className="rounded bg-surface px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-zinc-400 uppercase">
                      {result.command}
                    </span>
                  )}
                  {result.requestType === "sql" && sqlRowCount != null && (
                    <span className="text-xs text-zinc-500">{sqlRowCount} rows</span>
                  )}
                  {responseSize && (
                    <span className="text-xs text-zinc-600">{responseSize}</span>
                  )}
                </>
              )}
              {shownValue && (
                <>
                  {isJsonResponse && (
                    <button
                      onClick={handlePrettifyResult}
                      className="rounded p-1 text-zinc-600 hover:bg-surface-raised hover:text-zinc-400"
                      title={t("Prettify JSON")}
                    >
                      <Braces size={12} />
                    </button>
                  )}
                  {debug && (
                    <CopyButton text={buildCurl(debug)} label="cURL" />
                  )}
                  <button
                    onClick={handleCopy}
                    className="rounded p-1 text-zinc-600 hover:bg-surface-raised hover:text-zinc-400"
                    title={t("Copy result")}
                  >
                    {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Collapsible Request section */}
          {!isExecuting && debug && (
            <Section
              title={t("Request")}
              badge={
                <span className="ml-1.5 rounded bg-warning-bg px-1.5 py-0.5 text-[9px] font-bold normal-case tracking-normal text-warning">
                  {debug.request.method}
                </span>
              }
            >
              <div className="mb-2 break-all font-mono text-[11px] text-zinc-300">
                {debug.request.url}
              </div>
              <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-600">{t("Headers")}</div>
              <HeadersTable headers={debug.request.headers} />
              {debug.request.body && (
                <div className="mt-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-600">{t("Body")}</span>
                    <CopyButton text={debug.request.body} />
                  </div>
                  <pre className="max-h-32 overflow-auto rounded bg-surface-raised p-2 font-mono text-[11px] leading-relaxed text-zinc-400">
                    {tryFormatJson(debug.request.body)}
                  </pre>
                </div>
              )}
            </Section>
          )}

          {/* Collapsible Response Headers section */}
          {!isExecuting && hasResponseHeaders && (
            <Section
              title={t("Response Headers")}
              badge={
                <span className="ml-1 text-[9px] font-normal normal-case tracking-normal text-zinc-600">
                  ({Object.keys(debug!.response!.headers).length})
                </span>
              }
            >
              <HeadersTable headers={debug!.response!.headers} />
            </Section>
          )}

          {/* Main content */}
          <div className="flex flex-1 flex-col overflow-hidden bg-surface">
            {isExecuting && (
              <div className="flex h-full items-center justify-center">
                <div className="flex items-center gap-2 text-sm text-zinc-500">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-600 border-t-warning" />
                  Executing...
                </div>
              </div>
            )}

            {error && !isExecuting && (
              <div className="p-4">
                {isCorsLikeError(error) ? (
                  <div className="space-y-3 rounded bg-danger-bg p-3">
                    <div className="flex items-start gap-2.5 text-danger">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <div className="space-y-2">
                        <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{error}</pre>
                        <p className="text-xs text-zinc-400">
                          {t("The server doesn't allow cross-origin requests from the browser.")}
                          {" "}
                          {t("Use the Local Runtime to run requests locally without browser CORS limits.")}
                        </p>
                        <button
                          onClick={() => setShowRuntimeDialog(true)}
                          className="rounded bg-brand px-3 py-1 text-xs font-medium text-zinc-900 hover:bg-brand-light"
                        >
                          {t("Open Local Runtime")}
                        </button>
                        <p className="text-[10px] text-zinc-600">
                          {t("Install and pair the Local Runtime to route requests locally instead of through the browser.")}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2.5 rounded bg-danger-bg p-3 text-danger">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{error}</pre>
                  </div>
                )}
              </div>
            )}

            {!isExecuting && !error && gqlErrors && (
              <div className="shrink-0 border-b border-border">
                {gqlErrors.map((err, i) => (
                  <div key={i} className="flex gap-2.5 border-b border-border/50 px-4 py-2.5 last:border-b-0">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0 text-danger" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-danger">{String(err.message)}</div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-zinc-500">
                        {err.locations?.map((loc, j) => (
                          <span key={j} className="font-mono">line {loc.line}:{loc.column}</span>
                        ))}
                        {err.path && <span className="font-mono">{err.path.map((part) => String(part)).join(".")}</span>}
                        {err.extensions?.code !== undefined && (
                          <span className="rounded bg-danger-bg px-1 py-0.5 font-mono text-danger">
                            {String(err.extensions.code)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isExecuting && !error && sqlRows && (
              <LazyRelationalResultGrid rows={sqlRows} />
            )}

            {!isExecuting && !error && shownValue && !sqlRows && (
              isJsonResponse ? (
                <CodeMirrorEditor value={shownValue} readOnly extensions={jsonExtensions} />
              ) : (
                <CodeMirrorEditor value={shownValue} readOnly extensions={[]} />
              )
            )}

            {!isExecuting && !error && !result && (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-zinc-700">
                <Terminal size={28} />
                <div className="text-center text-sm">{t("Execute a query to see results")}</div>
                <kbd className="rounded border border-zinc-800 bg-surface-raised px-2 py-0.5 text-xs text-zinc-600">⌘ + Enter</kbd>
              </div>
            )}
          </div>
        </div>
      </SplitPane>

      {showSaveDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setShowSaveDialog(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-border bg-surface-raised p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="text-lg font-semibold text-zinc-100">{t("Save Query")}</div>
            <p className="mt-1 text-sm text-zinc-500">
              {t("Create a saved query from this panel.")}
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Name")}</label>
                <input
                  type="text"
                  value={saveName}
                  onChange={(event) => setSaveName(event.target.value)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-zinc-100 focus:border-brand focus:outline-none"
                  placeholder={t("My Query")}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && (saveCategorySelection !== NEW_CATEGORY_OPTION || saveCategory.trim())) {
                      event.preventDefault();
                      void handleSaveQuery();
                    }
                  }}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Save To")}</label>
                <select
                  value={saveCategorySelection}
                  onChange={(event) => setSaveCategorySelection(event.target.value)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-zinc-100 focus:border-brand focus:outline-none"
                >
                  <option value={NEW_CATEGORY_OPTION}>{t("New category")}</option>
                  {savedCategoryOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {saveCategorySelection === NEW_CATEGORY_OPTION && (
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Category")}</label>
                  <input
                    type="text"
                    value={saveCategory}
                    onChange={(event) => setSaveCategory(event.target.value)}
                    placeholder={t("Saved")}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-zinc-100 focus:border-brand focus:outline-none"
                  />
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Risk")}</label>
                <select
                  value={saveRiskLevel}
                  onChange={(event) => setSaveRiskLevel(event.target.value as QueryRiskLevel)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-zinc-100 focus:border-brand focus:outline-none"
                >
                  {QUERY_RISK_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <div className="mt-1 text-xs text-zinc-500">
                  {QUERY_RISK_OPTIONS.find((option) => option.value === saveRiskLevel)?.note}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Remarks")}</label>
                <textarea
                  value={saveRemarks}
                  onChange={(event) => setSaveRemarks(event.target.value)}
                  placeholder={t("Short guidance for agents and future you.")}
                  rows={4}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-zinc-100 focus:border-brand focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm text-zinc-300 hover:bg-surface"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => void handleSaveQuery()}
                disabled={!saveName.trim() || (saveCategorySelection === NEW_CATEGORY_OPTION && !saveCategory.trim())}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-zinc-950 hover:bg-brand-light"
              >
                {isSavingCopy ? t("Save Copy") : t("Save Query")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
